import Vue from 'vue';
import Router from 'vue-router';
import RouteHandler from './RouteHandler.vue';
import { isExperienceEditorActive } from '@sitecore-jss/sitecore-jss-vue';
import { config } from './auth/config';
import AuthRedirect from './auth/AuthRedirect';
import Logout from './components/Logout/Logout';
import RefreshToken from './components/RefreshToken/RefreshToken';
import { downloadDocument, downloadAnnualStatement } from './api/index';
import { logOutRequired, setCorrelationId } from './lib/UserSession.js';
import analyticsPlugins from './analytics/index.js';
import challengerEnv from './lib/challengerEnv';

// !important, we need conditional import with OKTA as Content editing functionality will be using SSR and does not have the window context.
//import Auth from '@okta/okta-vue';


const unauthenticatedRoutes = [
  '/login',
  '/forgotuserid',
  '/implicit/callback',
  '/error',
  '/logout',
  '/refreshtoken',
  '/maintenance.html',
];
const unauthenticatedRoutesEnforceTimeout = ['/login'];

const createRouter = (baseRoute = '/', envConfig) => {
  if (typeof window != 'undefined') {
    const Auth = require('@okta/okta-vue');
    Vue.use(Auth.default, config(envConfig).oidc);
  }
  Vue.use(Router);
  // create an instance of vue-router and configure routes to use the RouteHandler component
  const router = new Router({
    mode: 'history',
    base: baseRoute,
    scrollBehavior() {
      return { x: 0, y: 0 };
    },
    routes: [
      {
        path: '/implicit/callback',
        component: AuthRedirect,
      },
      // This route will Logout the user
      {
        path: '/logout',
        component: Logout,
        meta: {
          requiresAuth: false,
        },
      },
      // This route need to use the requiesAuth to ensure this refresh the token automatically
      // when refreshing the token - page will be automatically redirected to implicit callback...
      // this route cannot have any component containnig OKTA interaction as it will cause errors since tokens are not refreshed yet
      {
        path: '/refreshtoken',
        component: RefreshToken,
        meta: {
          requiresAuth: false,
        },
      },
      // This is a fake route for downloading document
      // Having a route will help with adobe analytics tracking
      // Here the behaviour will be to ABORT navigation and trigger download document...
      // IN case of direct access - the document will still download and user will be redirected to correspondence page
      // IN case of error - user will be redirected to the error Page
      {
        path: '/documents/:doc_id',
        component: {},
        meta: {
          requiresAuth: true,
        },
        beforeEnter: async (to, from, next) => {
          // avoid redirecting - instead trigger download DO NOT add Next()
          try {
            await downloadDocument(to.params.doc_id, to.query.doc_name);
            // If Direct Access to the Download document - redirect to Correspondence
            if (from == null || from.path === '/') {
              next('/correspondence');
            } else {
              analyticsPlugins(Vue.prototype.$logger).customWatcher('document-download', {
                docName: to.query.doc_name,
                docType: to.query.doc_type,
              });
              // This trigger a navigation cancel event
              // in Vue this will result in exception throw and we need to check the failure: aborted (https://router.vuejs.org/guide/advanced/navigation-failures.html#detecting-navigation-failures)
              next(false);
            }
          } catch (exception) {
            // in case of error - display the error page
            next('/error');
            if (Vue.prototype.$logger != null && typeof Vue.prototype.$logger !== 'undefined') {
              Vue.prototype.$logger.error(JSON.stringify(exception));
            }
          }
        },
      },
      // This is a fake route for downloading Annual Statement
      // Having a route will help with adobe analytics tracking
      // Here the behaviour will be to ABORT navigation and trigger download document...
      // IN case of error - user will be redirected to the error Page
      {
        path: '/statements/:account_no',
        component: {},
        meta: {
          requiresAuth: true,
        },
        beforeEnter: async (to, from, next) => {
          // avoid redirecting - instead trigger download DO NOT add Next()
          try {
            // We need to know if this statement is available or not.
            // downloadAnnualStatement will return true (download doc is possible) or false (document not available) or throw the exception on any other error
            let documentAvailable = await downloadAnnualStatement(
              to.params.account_no,
              to.query.doc_name
            );
            // Document is not available
            if (!documentAvailable) {
              from.meta.docAvailable = false;
            } else {
              from.meta.docAvailable = true;
              analyticsPlugins(Vue.prototype.$logger).customWatcher('document-download', {
                docName: to.query.doc_name,
                docType: 'periodic statements',
              });
            }
            // This trigger a navigation cancel event
            next(false);
          } catch (exception) {
            // in case of error - display the error page
            next('/error');
            if (Vue.prototype.$logger != null && typeof Vue.prototype.$logger !== 'undefined') {
              Vue.prototype.$logger.error(JSON.stringify(exception));
            }
          }
        },
      },
      ...authenticatedSitecoreRoutes,
    ],
  });
  router.beforeEach(onAuthRequired);
  router.beforeResolve(onResolve);
  return router;
};

// We need to execute the correlation ID after the  onAuthRequired - this is to ensure the user is logged in correctly and we have access to $auth
const onResolve = async (to, from, next) => {
  try {
    setCorrelationId(Vue.prototype.$auth, Vue.prototype.$envConfig);
  } catch (ex) {
    if (Vue.prototype.$logger != null && typeof Vue.prototype.$logger !== 'undefined') {
      Vue.prototype.$logger.error(ex);
    }
  }
  next();
};

const onAuthRequired = async (from, to, next) => {
  // Handle the route for experience editor or if the required flag is setup to false
  if (isExperienceEditorActive() || challengerEnv().mockApiResponses === true ) {
    if (from.path.toLowerCase() !== '/index.html'){
      next();
      return;
    }
    else{
      next('/')
      return;
    }
  }

  // Before doing anything, we need to check if you need to initiate LOGOUT
  // IF user session timed out (after 10 mins of inactivity on UI)
  // User refreshed without interacting with the page in UI...
  // User navigate to a page after his session timed out.
  // !important:
  //        - This needs to happen before checking the renew token - If we dont have this the flow will go to the refresh token and will trigger the login flow and restart a session
  //        - This will only happen for protectted routes (require user to be logged in) OR specific page defined in "unauthenticatedRoutesEnforceTimeout"
  if (Vue.prototype.$auth) {
    let logoutRequired = await logOutRequired(Vue.prototype.$auth);
    if (
      from.path.toLowerCase() !== '/logout' &&
      logoutRequired &&
      (unauthenticatedRoutesEnforceTimeout.includes(from.path) || // Exception routes: routes that can be accessed without login in but we want to redirect to logout if session timedout...
        !(
          unauthenticatedRoutes.includes(from.path) ||
          !from.matched.some((record) => record.meta.requiresAuth)
        )) // Authenticated route - need to be logged in
    ) {
      // stop the redirection go to Logout page
      next('/logout');
      return;
    }
  }

  // Get the Authentication state - we need to move this before the rest of the execution to ensure we rtrieve the user state before proceeding
  // for tracking and debugging purpose we are trying to pass the user id to all log and API - we need to ensure we have the authState even on non protected route.
  let authState = await getAuthState();

  // Unauthenticated routes - just navigate...
  if (
    unauthenticatedRoutes.includes(from.path) ||
    !from.matched.some((record) => record.meta.requiresAuth)
  ) {
    next();
    return;
  }

  // if user is not authenticated - then return to login page - no need to refresh token
  if (!authState.authenticated) {
    next({ path: '/login' });
    return;
  }

  // For the routes that require authentication
  // TOKEN Can expired while the session is still valid.
  //     when the access token has expire this will trigger the OKTA RENEW TOKEN flow which will navigate out to OKTA and come back...
  //     we should not continue the normal routing and go to a refresh token page so we can handle the flow.
  //     If we continue to the normal page and the page uses any user details then those will throw an error and the popup error will display...
  //     So instead we will redirect to a page with no comopnent "refreshtoken" - this will never fail or display error. Then OKTA flow will take over the renew token
  //     OKTA FLOW will redirect to the implicit callback page where we will check the session storage to know which page the user was going to access: FROM
  if (Vue.prototype.$auth) {
    let accessToken = await Vue.prototype.$auth.getAccessToken();
    let idToken = await Vue.prototype.$auth.getIdToken();
    if (from.path.toLowerCase() !== '/refreshtoken') {
      if (
        accessToken == null ||
        typeof accessToken == 'undefined' ||
        idToken == null ||
        typeof idToken == 'undefined'
      ) {
        // we will set the redirect URL in the Refresh token page...
        next({ path: '/refreshtoken', query: { callbackUri: from.path } });
        return;
      }
    }
  }

  if (!authState.hasAgreedChallengerTerms && from.path.toLowerCase() == '/terms') {
    // we are trying to accesss terms and conditions already dont force /terms as this create a loop
    next();
    return;
  } else if (!authState.hasAgreedChallengerTerms) {
    // If trying to access another page then force the /terms
    next('/terms');
    return;
  } else if (from.path === '/terms') {
    // If agreed to the terms and conditions but still trying to access TC then redirect to home
    next({ path: '/' });
    return;
  }

  if (!Vue.prototype.$envConfig.features.ePost && from.path.toLowerCase() == '/epost') {
    next({ path: '/' });
    return;
  }

  next();
};

const unfoldSitecoreRoute = (accumulator, paths) => {
  if (!paths || paths.length == 0) {
    return accumulator;
  }
  const path = paths.shift();
  const newRoutes = [`/:lang([a-z]{2}-[A-Z]{2})${path}`, `/:lang([a-z]{2})${path}`, `${path}`].map(
    function(path) {
      return {
        path: path,
        component: RouteHandler,
        props: (route) => ({
          route,
        }),
        meta: {
          requiresAuth: true,
        },
      };
    }
  );
  return unfoldSitecoreRoute([...newRoutes, ...accumulator], paths);
};

const authenticatedSitecoreRoutes = unfoldSitecoreRoute([], ['/:sitecoreRoute*'], true);

const getAuthState = async () => {
  let result = {
    authenticated: false,
    hasAgreedChallengerTerms: false,
  };

  if (!Vue.prototype.$auth) {
    return result;
  }

  try {
    const userAuthenticated = await Vue.prototype.$auth.isAuthenticated();
    result.authenticated = userAuthenticated;

    if (userAuthenticated) {
      const userDetails = await Vue.prototype.$auth.getUser();
      Vue.prototype.$auth.userDetails = userDetails;
      result.hasAgreedChallengerTerms = Vue.prototype.$auth.userDetails.hasAgreedChallengerTerms;
    }
  } catch (exception) {
    // OKTA Could return exception threw any calls...
    // catching those as user would be considered not authenticated for any error in okta
    // return Unauthenticated
    if (Vue.prototype.$logger != null && typeof Vue.prototype.$logger !== 'undefined') {
      Vue.prototype.$logger.error(JSON.stringify(exception));
    }
    return {
      authenticated: false,
      hasAgreedChallengerTerms: false,
    };
  }

  return result;
};

export { createRouter, unauthenticatedRoutes, unauthenticatedRoutesEnforceTimeout };
